import { useQuery } from '@tanstack/react-query'

import { ReactNode } from 'react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui'
import { PageHeader } from '@/components/pos/PageHeader'
import { useSession, useSyncState } from '@/contexts/SessionContext'
import { rows } from '@/db/database'
import { useBranchTerminals, useOfficeQuery } from '@/hooks/features/office'
import { OfficeModel, ProductModel } from '@/http/models'
import { monthStart, ymd } from '@/lib/dates'
import { formatDateTime, formatMoney } from '@/utils'

const Kpi = ({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) => (
  <Card>
    <CardContent className="space-y-1 p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-heading-lg tabular-nums text-ink">{value}</p>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </CardContent>
  </Card>
)

/**
 * The branch at a glance (B14). Today's sales, open shifts and stock come from the device, which holds this branch's
 * recent orders from every terminal, so they show offline too, as of the last sync (§15.8 U8). Profit needs the server.
 */
export const DashboardPage = () => {
  const { can } = useSession()
  const sync = useSyncState()
  const { data: terminals = [] } = useBranchTerminals()
  const { data: local } = useQuery({
    queryKey: ['dashboard', sync.lastSyncedAt],
    queryFn: async () => {
      const today = new Date().toDateString()
      const orders = (await rows('orders').toArray()).filter((o) => o.status !== 'cancelled' && new Date(String(o.created_at)).toDateString() === today)
      const products = await ProductModel.getList()
      const items = products.success ? (products.data?.items ?? []) : []
      return {
        count: orders.length,
        total: orders.reduce((s, o) => s + Number(o.total ?? 0), 0),
        low: items.filter((p) => p.stock > 0 && p.stock <= p.low_stock_threshold).length,
        out: items.filter((p) => p.stock <= 0).length,
      }
    },
  })
  const { device } = useSession()
  const branchId = device?.branch_id ?? ''
  const canProfit = can('pos:view_profit_loss')
  const { data: month } = useOfficeQuery(
    ['pl', 'month'],
    () => OfficeModel.profitLoss({ branch_id: branchId, from_date: monthStart(), to_date: ymd(new Date()) }),
    canProfit
  )
  const days = month?.result ?? []
  const sum = (list: typeof days, field: string) => list.reduce((s, d) => s + Number(d[field] ?? 0), 0)
  const today = days.filter((d) => d.date === ymd(new Date()))
  const open = terminals.filter((t) => t.is_open)

  return (
    <div className="flex h-full flex-col">
      <PageHeader title="Dashboard" subtitle={sync.lastSyncedAt ? `This branch, as of ${formatDateTime(sync.lastSyncedAt)}` : 'This branch'} />
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 md:p-6">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Kpi label="Sales today" value={formatMoney(local?.total ?? 0)} hint={`${local?.count ?? 0} orders, all terminals`} />
          <Kpi label="Open shifts" value={`${open.length} / ${terminals.length}`} hint={open.map((t) => `T${t.unique_no}`).join(', ') || 'None open'} />
          <Kpi label="Low stock" value={local?.low ?? 0} hint="At or below the reorder level" />
          <Kpi label="Out of stock" value={local?.out ?? 0} hint="Zero or below (sold past stock)" />
        </div>
        {canProfit && (
          <Card>
            <CardHeader>
              <CardTitle>Profit</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <Kpi label="Revenue today" value={formatMoney(sum(today, 'sales_revenue'))} />
              <Kpi label="Gross profit today" value={formatMoney(sum(today, 'sales_revenue') - sum(today, 'purchase_cost'))} />
              <Kpi label="Revenue this month" value={formatMoney(sum(days, 'sales_revenue'))} />
              <Kpi label="Gross profit this month" value={formatMoney(sum(days, 'sales_revenue') - sum(days, 'purchase_cost'))} />
            </CardContent>
          </Card>
        )}
        {sync.pending > 0 && <p className="text-xs text-muted-foreground">{sync.pending} records on this device have not reached the server yet.</p>}
      </div>
    </div>
  )
}
